/*
Description: Reads the Stride calendar for Sync Cal (COM-503).
Stride's calendar loads from its own same-origin JSON API, so the day on
screen is fetched straight from /api/calendars/appointments/ with the
user's session.
Date: 2026-10-14
*/

const APPOINTMENTS_PATH = '/api/calendars/appointments/';

// Stride appointmentStatus codes, as labelled on the calendar card.
const STATUS_LABELS: Record<string, string> = {
  O: 'Confirmed',
  C: 'Checked In',
  U: 'Unmarked',
  N: 'No Show',
  L: 'Late Cancel',
  A: 'Cancelled'
};

// Stride's own cancellation set. These don't become visits, but each keeps
// its exact status for the sync result.
const CANCELLED_CODES = ['A', 'L', 'N'];

const PLACE_OF_SERVICE: Record<string, string> = {
  O: 'Office',
  T: 'Telehealth',
  H: 'Home'
};

export async function getSchedule() {
  const me = await getJSON('/auth/whoami/');
  if (!me?.id) {
    throw new Error('Log in to Stride, then click Sync Cal again.');
  }

  const date = getCalendarDate();
  const query = new URLSearchParams({
    startDate: date,
    endDate: date,
    practiceUsers: String(me.id)
  });
  const appointments = await getJSON(`${APPOINTMENTS_PATH}?${query}`);
  if (!Array.isArray(appointments)) {
    throw new Error('Could not read your Stride calendar.');
  }

  const rows = appointments
    .filter((appointment) => isAttendee(appointment, me.id))
    .map((appointment) => mapAppointment(appointment, date, me.timezone));
  const isCancelled = (row: any) =>
    CANCELLED_CODES.includes(row.details?.statusCode);

  return {
    date,
    appointments: rows.filter((row) => !row.skipped && !isCancelled(row)),
    cancelled: rows.filter((row) => !row.skipped && isCancelled(row)),
    skipped: rows.filter((row) => row.skipped)
  };
}

async function getJSON(path: string) {
  const response = await fetch(path, { credentials: 'include' });
  if (!response.ok) return null;
  try {
    return await response.json();
  } catch {
    return null;
  }
}

// The URL doesn't carry the date; the calendar's last appointments request does.
function getCalendarDate(): string {
  const today = localDate(new Date());
  const last = performance
    .getEntriesByType('resource')
    .map((entry) => entry.name)
    .filter((name) => name.includes(APPOINTMENTS_PATH))
    .pop();
  if (!last) return today;

  const params = new URL(last).searchParams;
  const start = params.get('startDate');
  const end = params.get('endDate');
  if (start && start === end) return start;
  // Week/month view: today if it's on screen.
  if (start && end && start <= today && today <= end) return today;
  throw new Error(
    'Switch your Stride calendar to Day view, then click Sync Cal again.'
  );
}

// COM-571: sync every visit the user attends, shared ones included.
// isPrimaryAttendee isn't reliable (Stride sends false on solo visits).
function isAttendee(appointment: any, userId: number): boolean {
  const attendees = appointment?.practiceUsers || [];
  if (!attendees.length) return true;
  return attendees.some((attendee: any) => attendee.id === userId);
}

function mapAppointment(appointment: any, date: string, timeZone?: string) {
  const patient = appointment?.patient || {};
  const scheduledAt = toWallClockTimestamp(appointment?.startDateUtc, timeZone);
  const firstName = clean(patient.firstName);
  const lastName = clean(patient.lastName);

  if (appointment?.appointmentType?.isEventType || !patient.id) {
    return { skipped: true, scheduledAt, reason: 'not a patient visit' };
  }
  if (!firstName || !lastName) {
    return {
      skipped: true,
      scheduledAt,
      reason: "couldn't read the patient name"
    };
  }

  const statusCode = clean(appointment?.appointmentStatus);
  const status = STATUS_LABELS[statusCode] || statusCode || undefined;
  const provider = (appointment?.practiceUsers || [])
    .map((attendee: any) => attendee?.practiceUser?.fullName)
    .filter(Boolean)
    .join(', ');

  return {
    appointmentId: appointment?.id != null ? String(appointment.id) : undefined,
    firstName,
    lastName,
    dob: clean(patient.dateOfBirth) || undefined,
    // Matches StrideTheraInject.getPatientInfo, which keys the patient on the
    // chart's full-name link.
    emrId: clean(patient.fullName) || `${firstName} ${lastName}`,
    date,
    scheduledAt,
    noteType: clean(appointment?.appointmentType?.name) || undefined,
    provider: provider || undefined,
    status,
    details: getCardDetails(appointment, status)
  };
}

function getDurationMinutes(appointment: any): number | null {
  const ms =
    new Date(appointment?.endDateUtc).getTime() -
    new Date(appointment?.startDateUtc).getTime();
  return Number.isFinite(ms) && ms > 0 ? Math.round(ms / 60000) : null;
}

// Everything the calendar card shows, kept on the visit.
function getCardDetails(appointment: any, status?: string) {
  const patient = appointment?.patient || {};
  return {
    stridePatientId: patient.id ?? null,
    preferredName: patient.preferredName ?? null,
    pronouns: patient.pronouns?.name ?? null,
    status: status ?? null,
    statusCode: appointment?.appointmentStatus ?? null,
    startAt: toTimestamp(appointment?.startDateUtc) ?? null,
    endAt: toTimestamp(appointment?.endDateUtc) ?? null,
    durationMinutes: getDurationMinutes(appointment),
    appointmentType: appointment?.appointmentType?.name ?? null,
    isInitialEvaluation:
      appointment?.appointmentType?.isInitialEvaluationType ?? null,
    caseId: appointment?.case ?? null,
    caseTitle: appointment?.caseTitle ?? null,
    location: appointment?.location?.description ?? null,
    locationDetails: appointment?.locationDetails ?? null,
    placeOfService:
      PLACE_OF_SERVICE[appointment?.placeOfService] ??
      appointment?.placeOfService ??
      null,
    paymentType: appointment?.paymentType?.name ?? null,
    chargeType: appointment?.paymentType?.defaultChargeType?.name ?? null,
    isInsurance: appointment?.paymentType?.isInsurance ?? null,
    cancellationReason: appointment?.cancellationReason?.name ?? null,
    cancellationDetails: appointment?.cancellationDetails ?? null,
    rescheduled: appointment?.appointmentRescheduled ?? null,
    recurring: appointment?.recurringAppointment != null,
    isPending: appointment?.isPending ?? null,
    title: appointment?.title ?? null,
    description: appointment?.description ?? null,
    tags: appointment?.tags ?? null,
    attendees: (appointment?.practiceUsers || []).map((attendee: any) => ({
      id: attendee?.id ?? null,
      name: attendee?.practiceUser?.fullName ?? null,
      primary: attendee?.isPrimaryAttendee ?? null
    })),
    createdBy: appointment?.createdBy ?? null,
    modifiedBy: appointment?.modifiedBy ?? null
  };
}

// Stride sends UTC with a Z; conversations store UTC without it.
// Stride shows times in the practice timezone and our visit list in the
// browser's, so keep Stride's wall-clock time (same as Raintree).
function toWallClockTimestamp(
  value: any,
  timeZone?: string
): string | undefined {
  const start = new Date(value);
  if (!value || Number.isNaN(start.getTime())) return undefined;
  let parts: Record<string, number>;
  try {
    parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric'
      })
        .formatToParts(start)
        .filter((part) => part.type !== 'literal')
        .map((part) => [part.type, Number(part.value)])
    );
  } catch {
    return toTimestamp(value);
  }
  const local = new Date(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute
  );
  return local.toISOString().slice(0, -1);
}

function toTimestamp(value: any): string | undefined {
  if (!value) return undefined;
  const start = new Date(value);
  if (Number.isNaN(start.getTime())) return undefined;
  return start.toISOString().slice(0, -1);
}

function localDate(value: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
}

function clean(value: any): string {
  return String(value ?? '').trim();
}
